import React, { useEffect, useState, useCallback } from 'react'
import { Container, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Icon } from '@mdi/react'
import { Icon as Iconify } from '@iconify/react'
import { mdiBarrel, mdiArrowUpBold, mdiArrowDownBold } from '@mdi/js'

const getTrendIcon = (trend) => {
  switch (trend) {
    case 'up':
      return <Icon path={mdiArrowUpBold} size={0.7} color='#2e7d32' />
    case 'down':
      return <Icon path={mdiArrowDownBold} size={0.7} color='#d32f2f' />
    case 'same':
    default:
      return (
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#1976d2', display: 'inline-block' }} />
      )
  }
}

const StyledSubTitle = styled(Box)({
  fontSize: '14px',
  marginTop: '3px',
  marginLeft: '10px',
})

const StyledTitle = styled(Box)({
  display: 'flex',
  fontSize: '14px',
  marginTop: '3px',
  fontWeight: 'bold',
  justifyContent: 'right',
})

const StyledColContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
})

const OilPriceCard = ({ brentOilPrice, wtiOilPrice, naturalGassPrice, rigCounts, color }) => {
  const [oilPrices, setOilPrices] = useState({})
  const [rigCount, setRigCount] = useState([])

  const getOilPrices = useCallback(() => {
    if (brentOilPrice && wtiOilPrice && naturalGassPrice) {
      let brentDate = new Date(brentOilPrice?.created_at)
      let wtiDate = new Date(wtiOilPrice?.created_at)
      let naturalGasDate = new Date(naturalGassPrice?.created_at)
      brentOilPrice.localTime = brentDate?.toLocaleTimeString()
      wtiOilPrice.localTime = wtiDate?.toLocaleTimeString()
      naturalGassPrice.localTime = naturalGasDate?.toLocaleTimeString()
      setOilPrices({ brent: brentOilPrice, wti: wtiOilPrice, naturalGas: naturalGassPrice })
    }
  }, [brentOilPrice, naturalGassPrice, wtiOilPrice])  

  const getRigCounts = useCallback(() => {
    if (rigCounts && Array.isArray(rigCounts) && rigCounts.length > 0) {
      let rigCountData = []
      for (let i = 0; i < rigCounts.length; i++) {
        let region = rigCounts[i].region
        let count = rigCounts[i].count
        let date = new Date(rigCounts[i].date)
        let trend = rigCounts[i].trend

        rigCountData.push({
          region: region,
          count: count,
          date: date.toLocaleDateString(),
          trend: trend,
        })
      }

      setRigCount(rigCountData)
    }
  }, [rigCounts])

  useEffect(() => {
    getOilPrices()
    getRigCounts()
  }, [getOilPrices, getRigCounts]) 

  return (
    <React.Fragment>
      <Container
        sx={{
          '&': {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingVertical: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid gray',
            color: color,
          },
        }}>
        <Box sx={{ font: '1em roboto', fontWeight: 'bold', py: '8px' }}>Oil Price</Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Icon path={mdiBarrel} size={1} color={color} style={{ marginRight: '8px' }}></Icon>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'right' }}>
              <StyledTitle>Brent:</StyledTitle>
              <StyledTitle>WTI:</StyledTitle>
              <StyledTitle>Natural Gas:</StyledTitle>
            </Box>
            <StyledColContainer>
              <StyledSubTitle>{oilPrices?.brent?.formatted}</StyledSubTitle>
              <StyledSubTitle>{oilPrices?.wti?.formatted}</StyledSubTitle>
              <StyledSubTitle>{oilPrices?.naturalGas?.formatted}</StyledSubTitle>
            </StyledColContainer>

            <StyledColContainer>
              <StyledSubTitle>{oilPrices?.brent?.localTime}</StyledSubTitle>
              <StyledSubTitle>{oilPrices?.wti?.localTime}</StyledSubTitle>
              <StyledSubTitle>{oilPrices?.naturalGas?.localTime}</StyledSubTitle>
            </StyledColContainer>

            <StyledColContainer>
              <StyledSubTitle>{getTrendIcon(oilPrices?.brent?.trend)}</StyledSubTitle>
              <StyledSubTitle>{getTrendIcon(oilPrices?.wti?.trend)}</StyledSubTitle>
              <StyledSubTitle>{getTrendIcon(oilPrices?.naturalGas?.trend)}</StyledSubTitle>
            </StyledColContainer>
          </Box>
        </Box>

        {rigCount.length > 0 && (
          <>
            <Box sx={{ font: '1em roboto', fontWeight: 'bold', py: '8px' }}>Rig Count</Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Iconify icon='game-icons:oil-rig' style={{ marginRight: '8px', fontSize: '24px', color: color }} />
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'right' }}>
                  {rigCount.map((rig, index) => (
                    <StyledTitle key={`region-${index}`}>{rig.region}:</StyledTitle>
                  ))}
                </Box>
                <StyledColContainer>
                  {rigCount.map((rig, index) => (
                    <StyledSubTitle key={`count-${index}`}>{rig.count}</StyledSubTitle>
                  ))}
                </StyledColContainer>

                <StyledColContainer>
                  {rigCount.map((rig, index) => (
                    <StyledSubTitle key={`date-${index}`}>{rig.date}</StyledSubTitle>
                  ))}
                </StyledColContainer>

                <StyledColContainer>
                  {rigCount.map((rig, index) => (
                    <StyledSubTitle key={`trend-${index}`}>{getTrendIcon(rig.trend)}</StyledSubTitle>
                  ))}
                </StyledColContainer>
              </Box>
            </Box>
          </>
        )}
      </Container>
    </React.Fragment>
  )
}

export default OilPriceCard
